import {useCallback, useEffect, useState} from 'react';

import useFilters from '@/hooks/useFilters';
import {processorService} from '@/services/processorService';
import type {AfriexQueryParams} from '@/types';

import type {AfriexTestTransaction, TestTransactionListProps} from './types';
import {PAGE_SIZE} from './types';

type LoadedPage = {
  key: string;
  list: AfriexTestTransaction[];
  total: number;
  error: unknown;
};

/**
 * Processors -> Test Transactions. The filters behave like the retail
 * transaction list (status applies at once, the rest are staged in the panel
 * and applied together); the request bookkeeping is useNameResolutionList's:
 * `requestKey` names the filters + page on screen, and the list is "loading"
 * until a response for that key lands.
 */
const useTestTransactionList = (): TestTransactionListProps => {
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const [pageRequest, setPageRequest] = useState({filterKey: '', page: 0});
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState<LoadedPage>({
    key: '',
    list: [],
    total: 0,
    error: null,
  });
  const {applyFilters} = useFilters(setCurrentFilters);

  const filterKey = JSON.stringify(currentFilters);
  // Any filter change starts again from the first page.
  const page = pageRequest.filterKey === filterKey ? pageRequest.page : 0;
  const requestKey = `${filterKey}#${page}#${attempt}`;

  useEffect(() => {
    // A response for filters that have since changed must not land.
    let isCurrent = true;
    processorService
      .getTestTransactions({...currentFilters, page, limit: PAGE_SIZE})
      .then(
        (response) => {
          if (isCurrent) {
            setLoaded({
              key: requestKey,
              list: response.data ?? [],
              total: response.total ?? 0,
              error: null,
            });
          }
        },
        (error: unknown) => {
          if (isCurrent) {
            setLoaded({key: requestKey, list: [], total: 0, error});
          }
        },
      );
    return () => {
      isCurrent = false;
    };
  }, [currentFilters, page, requestKey]);

  // Promise-returning to match Pagination's `onPageChange`; the fetch itself
  // runs in the effect above once the page request lands.
  const onPageChange = useCallback(
    ({page: nextPage}: {page?: number}): Promise<void> => {
      setPageRequest({filterKey, page: Number(nextPage ?? 0)});
      return Promise.resolve();
    },
    [filterKey],
  );

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  const handleApplyFilters = useCallback(
    () => applyFilters(filtersToApply),
    [applyFilters, filtersToApply],
  );

  const selectCurrentFilters = useCallback(
    (name: string, value: string | undefined) =>
      setCurrentFilters((prev) => ({...prev, [name]: value})),
    [],
  );

  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {...prev, [name]: value};
        setFilterCount(Object.values(updatedFilters).filter(Boolean).length);
        return updatedFilters;
      });
    },
    [],
  );

  const clearFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
  };

  const isCurrentPage = loaded.key === requestKey;

  return {
    error: isCurrentPage ? loaded.error : null,
    isLoading: !isCurrentPage,
    // Kept while another page of the same filters loads, so the pager stays
    // mounted on its current page.
    total: loaded.key.startsWith(`${filterKey}#`) ? loaded.total : 0,
    transactionList: isCurrentPage ? loaded.list : [],
    filterKey,
    onPageChange,
    retry,
    filterCount,
    currentFilters,
    filtersToApply,
    clearFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
  };
};

export default useTestTransactionList;
